/**
 * Iter 245 — Commandes fournisseur (Purchase Orders).
 *
 * Liste + modal détail + workflow statuts (draft → sent → confirmed → received → paid).
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ShoppingCart, Loader2, Send, CheckCircle2, Truck, Wallet, XCircle, Plus, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  draft: { label: "Brouillon", color: "#94a3b8" },
  sent: { label: "Envoyée", color: "#3b82f6" },
  confirmed: { label: "Confirmée", color: "#a855f7" },
  received: { label: "Reçue", color: "#10b981" },
  paid: { label: "Payée", color: "#22c55e" },
  cancelled: { label: "Annulée", color: "#ef4444" },
};

const PAYMENT_LABELS = {
  en_attente: { label: "En attente", color: "#f59e0b" },
  paye: { label: "Payé", color: "#22c55e" },
  litige: { label: "Litige", color: "#ef4444" },
};

const NEXT_STATUS = {
  draft: { next: "sent", label: "Envoyer", icon: Send },
  sent: { next: "confirmed", label: "Confirmer", icon: CheckCircle2 },
  confirmed: { next: "received", label: "Marquer reçue", icon: Truck },
  received: { next: "paid", label: "Marquer payée", icon: Wallet },
};

export default function PurchaseOrdersPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all"); // all, draft, sent, confirmed, received, paid, cancelled

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/purchase-orders");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (po_id) => {
    try {
      const r = await api.get(`/inventory/purchase-orders/${po_id}`);
      setDetail(r.data);
    } catch (e) { toast.error("Impossible de charger le détail"); }
  };

  const changeStatus = async (po_id, status, payment_state) => {
    try {
      await api.put(`/inventory/purchase-orders/${po_id}/status`, { status, payment_state });
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]?.label}`);
      setDetail(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = filter === "all" ? items : items.filter(i => i.status === filter);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="po-page">
      <PageHeader
        icon={<ShoppingCart />}
        title="Commandes fournisseur"
        description={`${items.length} commande(s) au total.`}
      />

      {/* Filtres */}
      <div className="flex flex-wrap gap-1.5">
        {["all", "draft", "sent", "confirmed", "received", "paid", "cancelled"].map((s) => {
          const isActive = filter === s;
          const count = s === "all" ? items.length : items.filter(i => i.status === s).length;
          const lbl = s === "all" ? "Toutes" : STATUS_LABELS[s].label;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isActive ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] border border-white/5 text-slate-400 hover:text-white"}`}
            >
              {lbl} <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500">
          Aucune commande fournisseur dans cette catégorie.
        </div>
      ) : (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="po-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left p-3">PO</th>
                <th className="text-left p-3">Fournisseur</th>
                <th className="text-right p-3">Lignes</th>
                <th className="text-right p-3">Total HT</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-left p-3">Paiement</th>
                <th className="text-left p-3">Créée le</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((po) => {
                const st = STATUS_LABELS[po.status] || STATUS_LABELS.draft;
                const ps = PAYMENT_LABELS[po.payment_state || "en_attente"];
                return (
                  <tr key={po.po_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`po-${po.po_id}`}>
                    <td className="p-3 font-mono text-[11px] text-slate-300">{po.po_id}</td>
                    <td className="p-3 text-white">{po.supplier_name}</td>
                    <td className="p-3 text-right text-slate-300">{po.lines?.length || 0}</td>
                    <td className="p-3 text-right font-bold text-white">{po.total_ht_eur?.toFixed(2)} €</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: `${st.color}20`, color: st.color }}>{st.label}</span></td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: `${ps.color}20`, color: ps.color }}>{ps.label}</span></td>
                    <td className="p-3 text-xs text-slate-500">{(po.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(po.po_id)} data-testid={`view-${po.po_id}`}><Eye className="w-3.5 h-3.5 mr-1.5" /> Détail</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Commande fournisseur — {detail?.po_id}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Fournisseur</div>
                  <div className="text-white font-bold">{detail.supplier?.name || "?"}</div>
                  {detail.supplier?.email && <div className="text-xs text-slate-400">{detail.supplier.email}</div>}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-slate-500">Total HT</div>
                  <div className="text-xl font-bold text-white">{detail.total_ht_eur?.toFixed(2)} €</div>
                </div>
              </div>
              <div className="bg-[#0b0811] border border-white/5 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-black/40 text-[10px] uppercase text-slate-500">
                    <tr><th className="text-left p-2">Produit</th><th className="text-right p-2">Qté</th><th className="text-right p-2">PU</th><th className="text-right p-2">Total</th></tr>
                  </thead>
                  <tbody>
                    {detail.lines?.map((l, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="p-2 text-white">{l.product_name}<span className="text-[10px] text-slate-500 ml-1">{l.product_sku}</span></td>
                        <td className="p-2 text-right">{l.qty}</td>
                        <td className="p-2 text-right text-slate-300">{l.unit_cost_eur?.toFixed(2)} €</td>
                        <td className="p-2 text-right font-bold">{(l.qty * l.unit_cost_eur).toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detail.notes && (
                <div className="text-xs text-slate-400 bg-white/[0.02] rounded p-2">📝 {detail.notes}</div>
              )}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Paiement :</span>
                  <select
                    value={detail.payment_state || "en_attente"}
                    onChange={(e) => changeStatus(detail.po_id, detail.status, e.target.value)}
                    data-testid="payment-select"
                    className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-xs"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="paye">Payé</option>
                    <option value="litige">Litige</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  {NEXT_STATUS[detail.status] && (() => {
                    const N = NEXT_STATUS[detail.status]; const Icon = N.icon;
                    return (
                      <Button size="sm" onClick={() => changeStatus(detail.po_id, N.next)} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]" data-testid="btn-next-status">
                        <Icon className="w-3.5 h-3.5 mr-1.5" /> {N.label}
                      </Button>
                    );
                  })()}
                  {detail.status !== "cancelled" && detail.status !== "paid" && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(detail.po_id, "cancelled")} className="text-red-400 hover:text-red-300" data-testid="btn-cancel">
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Annuler
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
